import React, { useState, useEffect, VideoHTMLAttributes } from 'react';

interface FallbackVideoProps extends VideoHTMLAttributes<HTMLVideoElement> {
  // Meta CDN URL — always tried first (canonical, freshest source).
  primary?: string | null;
  // Our archived copy in DO Spaces — used ONLY if `primary` fails to load.
  fallback?: string | null;
}

// Plays the Meta CDN URL first and transparently swaps to our archived Spaces
// copy only when Meta's URL no longer works. Meta video URLs expire after a
// while; when that happens the <video> fires `onError` and we fall back. We
// never start on the archive so a working Meta URL is always preferred.
export default function FallbackVideo({ primary, fallback, onError, ...rest }: FallbackVideoProps) {
  const [useFallback, setUseFallback] = useState(false);
  // Reset when the URLs change (e.g. the dialog switches to a different ad).
  useEffect(() => { setUseFallback(false); }, [primary, fallback]);

  const src = !useFallback && primary ? primary : (fallback || primary || undefined);
  if (!src) return null;

  return (
    <video
      // key forces a remount when the src swaps so the browser reloads it
      // rather than re-using the errored media element.
      key={src}
      src={src}
      onError={(e) => {
        if (!useFallback && primary && fallback) setUseFallback(true);
        onError?.(e);
      }}
      {...rest}
    />
  );
}
